import React, { ReactElement } from "react";

import { css, cx } from "@emotion/css";
import {
  Link,
  RegisteredRouter,
  ValidateLinkOptions,
  ValidateLinkOptionsArray,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useComponentWidth } from "@/hooks/useComponentSize.ts";
import { usePatientIdFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { ScreenSizes } from "@/theme/responsive.ts";
import { TooltipMenuAction } from "@/uiKit/atoms/TooltipMenu.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { isProd } from "@/utils/commonUtils.ts";

const MenuItems = [
  {
    id: "manage-patient-menu-monitoring",
    to: "/patients/$patientId",
    title: "patientNavigation.monitoring",
  },
  {
    id: "manage-patient-menu-medical-file",
    to: "/patients/$patientId/medical-file",
    title: "patientNavigation.medicalFile",
  },
  {
    id: "manage-patient-menu-settings",
    to: "/patients/$patientId/settings",
    title: "patientNavigation.settings",
  },
  ...(!isProd()
    ? ([
        {
          id: "manage-patient-menu-patient-management",
          to: "/patients/$patientId/patient-management",
          title: "patientNavigation.dataManagement",
        },
      ] as const)
    : []),
] satisfies ValidateLinkOptionsArray;

export type PatientMenuProps = {
  className?: string;
};
export const PatientMenu: React.FC<PatientMenuProps> = ({ className }) => {
  const [divRef, width] = useComponentWidth();
  const styles = useStyles(makeStyles);
  const small = width < ScreenSizes.SM;

  return (
    <div
      ref={ref => {
        divRef.current = ref;
      }}
      className={cx(styles.container, className, {
        [styles.menuSmall]: small,
      })}
    >
      {small && <PatientMenuSmall />}
      {!small && <PatientMenuFull />}
    </div>
  );
};

const PatientMenuSmall: React.FC = () => {
  const styles = useStyles(makeStyles);
  const id = usePatientIdFromURL();
  const { t } = useTranslation();
  const routeMatches = useMatchRoute();
  const selectedItem = MenuItems.find(item => routeMatches({ to: item.to }));

  return (
    <>
      {selectedItem ? (
        <MenuItem
          id={selectedItem.id}
          link={{
            to: selectedItem.to,
            params: { patientId: id },
          }}
          selected={true}
          title={t(selectedItem.title)}
        />
      ) : null}
      <TooltipMenuAction
        id="manage-patient-menu"
        className={styles.tooltipAction}
        wrapperClassName={styles.tooltipWrapper}
        placement="bottom-start"
        actions={MenuItems.map(item =>
          item.title !== selectedItem?.title ? (
            <SmallMenuItem
              id={item.id}
              key={item.title}
              title={t(item.title)}
              link={{
                to: item.to,
                params: { patientId: id },
              }}
            />
          ) : null,
        )}
      >
        <Icons.hamburger className={styles.icon} />
      </TooltipMenuAction>
    </>
  );
};

const PatientMenuFull: React.FC = () => {
  const id = usePatientIdFromURL();
  const { t } = useTranslation();
  const routeMatches = useMatchRoute();
  return (
    <>
      {MenuItems.map(item => (
        <MenuItem
          id={item.id}
          key={item.title}
          title={t(item.title)}
          selected={!!routeMatches({ to: item.to })}
          link={{
            to: item.to,
            params: { patientId: id },
          }}
        />
      ))}
    </>
  );
};

type MenuItemProps<TOptions> = {
  id: string;
  title: string;
  selected: boolean;
  link: ValidateLinkOptions<RegisteredRouter, TOptions>;
};

const MenuItem = <TOptions,>({ id, title, link, selected }: MenuItemProps<TOptions>): ReactElement => {
  const styles = useStyles(makeMenuItemStyles, selected);
  const navigate = useNavigate();

  return (
    <div id={id} className={styles.menuItem} onClick={() => navigate(link)}>
      <div className={styles.menuItemLink}>
        <Link {...link} />
        <Typography variant="subtitle" className={styles.text}>
          {title}
        </Typography>
      </div>
    </div>
  );
};

type SmallMenuItemProps<TOptions> = {
  id: string;
  title: string;
  link: ValidateLinkOptions<RegisteredRouter, TOptions>;
};

const SmallMenuItem = <TOptions,>({ id, title, link }: SmallMenuItemProps<TOptions>): ReactElement => {
  const styles = useStyles(makeMenuItemStyles, false);

  return (
    <Link id={id} {...link}>
      <Typography variant="body" className={styles.text}>
        {title}
      </Typography>
    </Link>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
  `,
  tooltipAction: css({
    display: "flex",
    alignSelf: "stretch",
    alignItems: "center",
  }),
  tooltipWrapper: css({
    display: "flex",
  }),
  menuSmall: css`
    align-items: center;
  `,
  icon: css`
    margin-left: ${theme.spacing(8)};
    width: ${FontSizes.body};
    height: ${FontSizes.body};
    color: ${theme.palette.text.primary};
  `,
});

const makeMenuItemStyles = (theme: Theme, selected: boolean) => ({
  menuItem: css`
    display: flex;
    flex-direction: column;
    cursor: pointer;
    border-radius: ${theme.spacing(2)} ${theme.spacing(2)} 0 0;

    &:hover {
      background-color: ${theme.palette.background.default};
      a {
        border-bottom: ${!selected ? `${theme.palette.background.paper} ${theme.spacing(1)} solid` : ""};
      }
    }
  `,
  menuItemLink: css`
    flex-grow: 1;
    margin: ${theme.spacing(3)} ${theme.spacing(8)} 0;
    &::after {
      content: "";
      position: relative;
      display: ${selected ? "block" : "none"};
      left: 0;
      right: 0;
      bottom: 0;
      height: ${theme.spacing(1)};
      width: 100%;
      background: ${theme.palette.primary.main};
    }
  `,
  text: css`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: clip;
    font-weight: 600;
    color: ${selected ? theme.palette.primary.main : ""};
  `,
});
